import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import { queryKeys } from '../../../shared/api/query-keys';

/* Espelha `TaxProfileResponse`: o campo e `ncmCode`, nao `ncmPrefix` — a
   coluna NCM da tela de perfis fiscais vinha vazia. */
/** Espelha `TaxProfileResponse` (tax-profiles.controller.ts), campo a campo. */
export interface TaxProfile {
  id: string;
  ncmCode: string | null;
  /** Perfil de servico — o tipo anterior so cobria produto. */
  serviceId: string | null;
  nbsCode: string | null;
  gtinCode: string | null;
  productId: string | null;
  classificacaoTributaria: string | null;
  reducaoIbs: number | null;
  reducaoCbs: number | null;
  cstIbsCbs: string | null;
  tributacaoRegular: boolean;
  creditoPresumido: boolean;
  monofasico: boolean;
  diferimento: boolean;
  artigo: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Um item esta classificado para IBS/CBS quando tem CST e classificacao
 * tributaria. Sem os dois o motor nao sabe como tratar o item no novo modelo.
 */
export function isMappedForReform(p: Pick<TaxProfile, 'cstIbsCbs' | 'classificacaoTributaria'>): boolean {
  return Boolean(p.cstIbsCbs && p.classificacaoTributaria);
}

export interface TaxProfileInput {
  ncmCode?: string | null;
  productId?: string | null;
  classificacaoTributaria?: string | null;
  reducaoIbs?: number | null;
  reducaoCbs?: number | null;
  cstIbsCbs?: string | null;
  monofasico?: boolean;
  diferimento?: boolean;
}

export function useTaxProfiles() {
  return useQuery({
    queryKey: queryKeys.fiscal.taxProfiles(),
    queryFn: () => apiClient.get<TaxProfile[]>('/fiscal/tax-profiles'),
  });
}

export function useCreateTaxProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaxProfileInput) => apiClient.post<TaxProfile>('/fiscal/tax-profiles', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxProfiles() }),
  });
}

export function useUpdateTaxProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: TaxProfileInput & { id: string }) =>
      apiClient.put<TaxProfile>(`/fiscal/tax-profiles/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxProfiles() }),
  });
}

export function useDeleteTaxProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/fiscal/tax-profiles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxProfiles() }),
  });
}
